import { useLazyGetWelcomeMessageQuery, useSendMessageMutation } from 'store/services/core/chatbot';
import { useGetAssistantsQuery } from 'store/services/core/assistants';
import type { ChatHistory, Message as MessageType, ConversationMeta } from 'types/chatbot';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTemplateIdToModuleId } from 'utils/hooks/useTemplateIdToModuleId';
import { useDispatch, useSelector } from 'react-redux';
import { selectActiveHistoryId, selectFullScreen, selectHistory, selectOpened, selectSize } from 'store/entities/chatbot/selectors';
import { setActiveHistoryId, setHistory, setSize, toggleOpen } from 'store/entities/chatbot/actions';
import { Header } from './Header';
import { Message } from './Message';
import { Dialog, Messages, Resizer, StyledChatIconButton } from './index.styles';
import { Footer } from './Footer';

import { useInitiateByOldData } from 'utils/hooks/chatbot';
import { hasUserMessage, upsertChatHistory } from 'utils/chatbotHistory';
import { SmartToy } from '@mui/icons-material';
import { useGetModuleByIdQuery } from 'store/services/core/modules';

const MIN_W = 320;
const MIN_H = 360;
const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

const ChatbotBase = () => {
  useInitiateByOldData();

  const [conversation, setConversation] = useState<ConversationMeta>({ assistantId: null });

  const [messages, setMessages] = useState<MessageType[]>([]);
  const [input, setInput] = useState('');
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const boxRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch();
  const visible = useSelector(selectOpened());
  const fullScreen = useSelector(selectFullScreen());
  const size = useSelector(selectSize());
  const history = useSelector(selectHistory());
  const activeHistoryId = useSelector(selectActiveHistoryId());
  const historyRef = useRef(history);
  const draftHistoryIdRef = useRef<string | null>(null);
  const [localSize, setLocalSize] = useState(size);
  const [getWelcomeMessage] = useLazyGetWelcomeMessageQuery();
  const [sendChatMessage, { isLoading: sending }] = useSendMessageMutation();

  const { data: allAssistantsData, isLoading: isAllAssistantsLoading } = useGetAssistantsQuery();
  const moduleId = useTemplateIdToModuleId();
  const { data: moduleData, isLoading: isModuleLoading } = useGetModuleByIdQuery({ id: moduleId ?? '' }, { skip: !moduleId });
  const assistants = useMemo(() => {
    if (moduleId && moduleData) {
      return moduleData.assistants;
    }
    return allAssistantsData ?? [];
  }, [allAssistantsData, moduleData, moduleId]);

  const assistantsRef = useRef(assistants);

  useEffect(() => {
    setLocalSize(size);
  }, [size]);

  useEffect(() => {
    assistantsRef.current = assistants;
  }, [assistants]);

  useEffect(() => {
    if (assistants.length > 0 && (conversation.assistantId === null || !assistants.some((a) => a.id === conversation.assistantId))) {
      setConversation({ assistantId: assistants[0].id });
    }
  }, [assistants, conversation.assistantId]);

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    if (!visible) {
      const p = new URLSearchParams(window.location.search);
      if (p.get('chat') === '1') dispatch(toggleOpen());
    }
  }, [dispatch, visible]);

  useEffect(() => {
    if (visible && messages.length === 0 && !isAllAssistantsLoading && !isModuleLoading) {
      const fetchWelcome = async () => {
        try {
          const selectedAssistant = assistantsRef.current.find((a) => a.id === conversation.assistantId);
          const { data } = await getWelcomeMessage(selectedAssistant?.id);

          if (data?.welcome_message) {
            setMessages([{ role: 'assistant', content: data.welcome_message }]);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Failed to fetch welcome message:', error);
        }
      };
      fetchWelcome();
    }
  }, [visible, messages.length, getWelcomeMessage, conversation.assistantId, isAllAssistantsLoading, isModuleLoading]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const persistHistoryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (persistHistoryTimeoutRef.current) {
      clearTimeout(persistHistoryTimeoutRef.current);
      persistHistoryTimeoutRef.current = null;
    }

    if (!hasUserMessage(messages)) return;

    persistHistoryTimeoutRef.current = setTimeout(() => {
      const chatId = activeHistoryId ?? draftHistoryIdRef.current ?? Date.now().toString();
      draftHistoryIdRef.current = chatId;

      const currentChat: ChatHistory = {
        id: chatId,
        messages: [...messages],
        assistantId: conversation.assistantId,
        timestamp: Date.now(),
      };

      const existingChat = historyRef.current.find((item) => item.id === chatId);
      const isSameConversation =
        existingChat?.assistantId === currentChat.assistantId &&
        existingChat?.messages.length === currentChat.messages.length &&
        existingChat?.messages.every(
          (message, index) => message.role === currentChat.messages[index]?.role && message.content === currentChat.messages[index]?.content
        );

      if (isSameConversation) {
        persistHistoryTimeoutRef.current = null;
        return;
      }

      dispatch(setHistory(upsertChatHistory(historyRef.current, currentChat)));
      persistHistoryTimeoutRef.current = null;
    }, 250);

    return () => {
      if (persistHistoryTimeoutRef.current) {
        clearTimeout(persistHistoryTimeoutRef.current);
        persistHistoryTimeoutRef.current = null;
      }
    };
  }, [activeHistoryId, conversation.assistantId, dispatch, messages]);

  useEffect(() => {
    const save = () => {
      const el = boxRef.current;
      if (!el || fullScreen) return;
      const rect = el.getBoundingClientRect();
      const w = Math.round(rect.width);
      const h = Math.round(rect.height);
      dispatch(setSize({ w, h }));
    };
    window.addEventListener('mouseup', save);
    window.addEventListener('touchend', save);
    return () => {
      window.removeEventListener('mouseup', save);
      window.removeEventListener('touchend', save);
    };
  }, [dispatch, fullScreen]);

  const resize = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const startW = size.w;
    const startH = size.h;

    let curW = startW;
    let curH = startH;

    const onMove = (ev: MouseEvent) => {
      const dx = startX - ev.clientX;
      const dy = startY - ev.clientY;
      const maxW = Math.round(window.innerWidth * 0.95);
      const maxH = Math.round(window.innerHeight * 0.9);
      curW = clamp(startW + dx, MIN_W, maxW);
      curH = clamp(startH + dy, MIN_H, maxH);
      setLocalSize({ w: curW, h: curH });
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);

      dispatch(setSize({ w: curW, h: curH }));
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleNewChat = useCallback(() => {
    setMessages([]);
    setInput('');
    dispatch(setActiveHistoryId(null));
    draftHistoryIdRef.current = null;
  }, [dispatch]);

  const sendMessage = useCallback(async () => {
    const message = input.trim();
    if (!message || sending) return;
    setInput('');

    if (!activeHistoryId && !draftHistoryIdRef.current) {
      draftHistoryIdRef.current = Date.now().toString();
    }

    setMessages((prev) => [...prev, { role: 'user', content: message }]);

    try {
      const selectedAssistant = assistantsRef.current.find((a) => a.id === conversation.assistantId);
      const { message: assistantText = '' } = await sendChatMessage({
        message,
        history: messages,
        assistantId: selectedAssistant?.id,
      }).unwrap();

      setMessages((prev) => [...prev, { role: 'assistant', content: assistantText }]);
    } catch (error) {
      let detail = 'Error connecting to server.';

      const err = error as { data?: { ErrorDetails?: { Detail?: string } } };
      if (error instanceof Error) {
        detail = error.message;
      } else if (err?.data?.ErrorDetails?.Detail) {
        detail = err.data.ErrorDetails.Detail;
      }
      setMessages((prev) => [...prev, { role: 'assistant', content: `❌ ${String(detail)}` }]);
    }
  }, [activeHistoryId, conversation.assistantId, input, messages, sendChatMessage, sending]);

  const copyToClipboard = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <>
      <StyledChatIconButton onClick={() => dispatch(toggleOpen())} title="Chat" data-testid="admin-toolbar-chatbot-button">
        <SmartToy sx={{ fontSize: '25px' }} />
      </StyledChatIconButton>
      <Dialog
        open={visible}
        hideBackdrop
        disableEnforceFocus
        disablePortal={false}
        fullWidth
        maxWidth={false}
        fullScreen={fullScreen}
        size={localSize}>
        {!fullScreen && <Resizer onMouseDown={resize} />}
        <Header messages={messages} setMessages={setMessages} setConversation={setConversation} onNewChat={handleNewChat} />
        <Messages>
          {messages.map((message, index) => (
            <Message
              key={index}
              role={message.role}
              content={message.content}
              isCopied={copiedIndex === index}
              onCopy={(text) => copyToClipboard(text, index)}
            />
          ))}
          {sending && <Message role="assistant" isThinking={true} />}
          <div ref={bottomRef} />
        </Messages>
        <Footer
          assistants={assistants}
          selectedAssistantId={conversation.assistantId}
          onAssistantChange={(id) => {
            setConversation({ assistantId: id });
            handleNewChat();
          }}
          input={input}
          onInputChange={setInput}
          sending={sending}
          onSend={sendMessage}
        />
      </Dialog>
    </>
  );
};

export const Chatbot = memo(ChatbotBase);
